import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import * as codes from 'ducks/codes';
import CodeTypes from 'ducks/codes/types';

/**
 * Event types the current user is allowed to see.
 *
 * The server already strips the caller's event type exclusions
 * (per-user `Event Types` setting) from `GET /api/codes?type=EVENT TYPES`,
 * so the returned codes list is the authoritative "allowed" list for the
 * logged-in user. The dedicated `/users/{userID}/event-type-exclusions`
 * endpoint cannot be used here: it subtracts the caller's own exclusions and
 * therefore always answers with an empty list when a user asks about itself.
 *
 * `isLoaded` tells apart "codes were not fetched yet" from "codes were fetched
 * and the type is excluded", so callers can avoid hiding UI on first render.
 *
 * Pass `shouldFetch` from a single always-mounted consumer only (the main
 * header). Every other consumer reads the already loaded codes — the route
 * guards mount dozens of instances at once and must not each fire a request.
 */
export const useEventTypeAccess = ({ shouldFetch = false } = {}) => {
  const { requestCodes } = bindActionCreators(codes.actions, useDispatch());

  const eventTypes = useSelector((state) => codes.getCodes(state, CodeTypes.EVENT_TYPES));

  useEffect(() => {
    if (shouldFetch && !eventTypes) {
      requestCodes(CodeTypes.EVENT_TYPES);
    }
  }, [shouldFetch, eventTypes]);

  const hasEventType = useCallback(
    (code) => (eventTypes || []).some((item) => item.code === code),
    [eventTypes]
  );

  return useMemo(
    () => ({
      isLoaded: !!eventTypes,
      hasEventType,
    }),
    [eventTypes, hasEventType]
  );
};
